/**
 * Small dependency-free text diff for the import preview. Produces a line-level
 * diff (LCS) and, for replaced regions, an inline word-level diff so the reviewer
 * can see exactly what an external editor changed.
 */

type Kind = "eq" | "del" | "ins";

interface Seg<T> {
  type: Kind;
  value: T;
}

/** Longest-common-subsequence diff over an array of comparable tokens. */
function lcsDiff<T>(a: T[], b: T[], eq: (x: T, y: T) => boolean): Seg<T>[] {
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i]![j] = eq(a[i]!, b[j]!) ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
    }
  }
  const out: Seg<T>[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (eq(a[i]!, b[j]!)) {
      out.push({ type: "eq", value: a[i]! });
      i++;
      j++;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      out.push({ type: "del", value: a[i]! });
      i++;
    } else {
      out.push({ type: "ins", value: b[j]! });
      j++;
    }
  }
  while (i < n) out.push({ type: "del", value: a[i++]! });
  while (j < m) out.push({ type: "ins", value: b[j++]! });
  return out;
}

export interface WordPart {
  text: string;
  type: Kind;
}
export interface DiffRow {
  type: Kind;
  text: string;
  /** Inline word-level parts, present on replaced (paired del/ins) rows. */
  parts?: WordPart[];
}

function tokenizeWords(line: string): string[] {
  // Keep whitespace as its own tokens so spacing survives the diff.
  return line.match(/\s+|[^\s]+/g) ?? [];
}

function wordParts(oldLine: string, newLine: string, want: "del" | "ins"): WordPart[] {
  const segs = lcsDiff(tokenizeWords(oldLine), tokenizeWords(newLine), (x, y) => x === y);
  const parts: WordPart[] = [];
  for (const s of segs) {
    if (s.type === "eq") parts.push({ text: s.value, type: "eq" });
    else if (s.type === "del" && want === "del") parts.push({ text: s.value, type: "del" });
    else if (s.type === "ins" && want === "ins") parts.push({ text: s.value, type: "ins" });
  }
  return parts;
}

export function diffLines(oldText: string, newText: string): DiffRow[] {
  const a = oldText.replace(/\r\n?/g, "\n").split("\n");
  const b = newText.replace(/\r\n?/g, "\n").split("\n");
  const segs = lcsDiff(a, b, (x, y) => x === y);

  const rows: DiffRow[] = [];
  for (let k = 0; k < segs.length; k++) {
    const seg = segs[k]!;
    // Pair a del immediately followed by an ins into a word-level replacement.
    if (seg.type === "del" && segs[k + 1]?.type === "ins") {
      const next = segs[k + 1]!;
      rows.push({ type: "del", text: seg.value, parts: wordParts(seg.value, next.value, "del") });
      rows.push({ type: "ins", text: next.value, parts: wordParts(seg.value, next.value, "ins") });
      k++;
      continue;
    }
    rows.push({ type: seg.type, text: seg.value });
  }
  return rows;
}
